import { HeroParallax } from "@/components/ui/hero-parallax";
import { ThreeDMarquee } from "@/components/ui/3d-marquee";
import { PremiumProjectCard } from "@/components/PremiumProjectCard";
import { AnimatedTestimonials } from "@/components/ui/animated-testimonials";
import ProceduralGroundBackground from "@/components/ui/procedural-ground-background";
import ServicesGrid from "@/components/ServicesGrid";
import DevSection from "@/components/DevSection";
import { useFeaturedProjects, useProjects } from "@/hooks/use-projects";
import { useServices } from "@/hooks/use-services";
import { useTestimonials } from "@/hooks/use-testimonials";
import { useClients } from "@/hooks/use-clients";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  getClientLogo,
  getProjectCover,
  getTestimonialAvatar,
  isValidImageUrl
} from "@/data/mockImages";
import SEO, { homepageSchema } from "@/components/SEO";

const seoDescription = "Cássio Lemos - Portfólio 2026: Desenvolvedor Fullstack e Designer de Interface especializado em criar experiências digitais impactantes. Transforme sua visão em realidade com design premium, desenvolvimento de sites, branding e tecnologia de ponta. Serviços em Rio de Janeiro e todo Brasil.";

export default function Index() {
  const { data: allProjects, isLoading: allProjectsLoading } = useProjects();
  const { data: featuredProjects, isLoading: projectsLoading } = useFeaturedProjects();
  const { data: services, isLoading: servicesLoading, error: servicesError } = useServices();
  const { data: testimonials, isLoading: testimonialsLoading, error: testimonialsError } = useTestimonials();
  const { data: clients, isLoading: clientsLoading, error: clientsError } = useClients();

  const heroProducts = allProjects?.slice(0, 15).map((project) => ({
    title: project.title,
    link: `/projetos/${project.slug}`,
    thumbnail: isValidImageUrl(project.coverImage)
      ? project.coverImage
      : getProjectCover(project.slug),
  })) || [];

  while (heroProducts.length < 15 && heroProducts.length > 0) {
    heroProducts.push(...heroProducts.slice(0, 15 - heroProducts.length));
  }

  const clientImages = clients?.map((client) => ({
    src: isValidImageUrl(client.logo) ? client.logo : getClientLogo(client.name),
    alt: client.name,
  })) || [];

  const testimonialItems = testimonials?.map((t) => ({
    quote: t.quote,
    name: t.name,
    designation: `${t.role} • ${t.company}`,
    src: isValidImageUrl(t.avatar) ? t.avatar : getTestimonialAvatar(t.name),
  })) || [];

  const isLoading = allProjectsLoading || projectsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div role="status" aria-live="polite">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Desenvolvedor Fullstack & Designer de Interface"
        description={seoDescription}
        canonical="https://cassio.art/"
        ogType="website"
        schema={homepageSchema}
      />

      {heroProducts.length > 0 ? (
        <HeroParallax
          products={heroProducts}
          title="Cássio"
          subtitle="Diretor de Arte & Designer Gráfico"
          description="Especialista em IA, criação de personagens digitais, desenvolvimento fullstack e design 3D. Transformo ideias em experiências visuais impactantes que conectam marcas ao futuro digital."
        />
      ) : (
        <section className="py-32 bg-hero">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-7xl font-bold mb-4">Cássio</h1>
            <h2 className="text-2xl md:text-4xl font-bold gradient-text-primary">
              Diretor de Arte & Designer Gráfico
            </h2>
          </div>
        </section>
      )}

      {clientsLoading ? (
        <section className="py-20">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </section>
      ) : clientsError ? (
        <section className="py-20 text-center">
          <p className="text-muted-foreground">Erro ao carregar clientes.</p>
        </section>
      ) : clientImages.length > 0 ? (
        <section className="overflow-hidden">
          <div className="container mx-auto container-padding">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="heading-section mb-4 mt-[5%]">
                Clientes &{" "}
                <span className="gradient-text-primary">Parceiros</span>
              </h2>
              <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
                Tenho o privilégio de colaborar com marcas renomadas e empresas inovadoras em todo o Brasil
              </p>
            </motion.div>
          </div>
          <ThreeDMarquee images={clientImages} />
        </section>
      ) : null}

      <section className="pt-0 pb-20 relative overflow-hidden">
        <ProceduralGroundBackground className="absolute inset-0 w-full h-full z-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/50 z-0" />

        <div className="container mx-auto container-padding relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4 mt-[5%]">
              Serviços de <span className="gradient-text-primary">Design & Desenvolvimento</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Soluções completas para transformar sua presença digital: desde identidade visual estratégica até desenvolvimento de sistemas web de alta performance
            </p>
          </motion.div>

          {servicesLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : servicesError ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Erro ao carregar serviços. Tente novamente mais tarde.</p>
            </div>
          ) : services && services.length > 0 ? (
            <ServicesGrid services={services} />
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Nenhum serviço encontrado.</p>
            </div>
          )}
        </div>
      </section>

      <DevSection />

      <section className="section-padding bg-muted/30">
        <div className="container mx-auto container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Projetos em <span className="gradient-text-primary">Destaque</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Explore cases de sucesso de design gráfico, desenvolvimento web, e-commerce e muito mais. Cada projeto é uma história de transformação digital.
            </p>
          </motion.div>

          {featuredProjects && featuredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredProjects.slice(0, 6).map((project, index) => (
                <PremiumProjectCard
                  key={project.id}
                  id={project.id}
                  index={index}
                  title={project.title}
                  summary={project.summary}
                  coverImage={isValidImageUrl(project.coverImage) ? project.coverImage : getProjectCover(project.slug)}
                  slug={project.slug}
                  year={project.year}
                  client={project.client}
                  disciplines={project.disciplines}
                  techStack={project.tools}
                  projectType={
                    project.disciplines?.some((d: string) =>
                      ['Desenvolvimento', 'Dev', 'Code', 'Full Stack'].some(term => d.includes(term))
                    ) ? 'dev' : 'design'
                  }
                  demoUrl={null}
                  githubUrl={null}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Nenhum projeto em destaque encontrado.</p>
            </div>
          )}

          <div className="text-center">
            <Button asChild size="lg" className="liquid-glass-btn-solid">
              <Link to="/projetos">
                Ver Todos os Projetos
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {testimonialsLoading ? (
        <section className="py-20">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </section>
      ) : testimonialsError ? (
        <section className="py-20 text-center">
          <p className="text-muted-foreground">Erro ao carregar depoimentos.</p>
        </section>
      ) : testimonialItems.length > 0 ? (
        <section className="section-padding">
          <div className="container mx-auto container-padding">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="heading-section mb-4">
                O Que Dizem Sobre <span className="gradient-text-primary">Meu Trabalho</span>
              </h2>
              <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
                Depoimentos autênticos de clientes satisfeitos que confiaram em meu trabalho para transformar suas marcas
              </p>
            </motion.div>
            <AnimatedTestimonials testimonials={testimonialItems} autoplay />
          </div>
        </section>
      ) : null}

      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 gradient-bg-subtle" />
        <div className="container mx-auto container-padding text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-section mb-4">
              Pronto para <span className="gradient-text-primary">Transformar</span> Sua Marca?
            </h2>
            <p className="text-body-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
              Vamos colaborar para criar uma identidade visual única e estratégica que conecte com seu público e impulsione os resultados do seu negócio
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="liquid-glass-btn-solid">
                <Link to="/contato">
                  Solicitar Orçamento Grátis
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="liquid-glass-outline">
                <Link to="/projetos">
                  Ver Portfólio Completo
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
